import moment from 'moment';
import { checkIsFutureDate } from 'shared-utils';
import { useToast } from 'src/context/ToastProvider';
import { useMutation, useQueryClient } from 'react-query';
import React, { FormEvent, useEffect, useState } from 'react';
import { UpdateWorkExperienceInput, WorkExperienceDetail } from 'types';
import { Button, Label, Modal, Spinner, TextInput, Textarea } from 'flowbite-react';
import { createUserWorkExperienceFn, editUserWorkExperienceFn } from 'src/apis/profile';
import { get } from 'lodash';

type Props = {
	isOpen: boolean;
	onClose: () => void;
	workExperience: WorkExperienceDetail;
	isEditing?: boolean;
	workExpId: string;
};

const EditProfileWorkExperience = ({ workExperience, workExpId, isOpen, onClose, isEditing }: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [workExId, setWorkExId] = useState('');
	const [dateError, setDateError] = useState<string>('');
	const [userWorkExperience, setUserWorkExperience] = useState<UpdateWorkExperienceInput>({
		updateInfo: {
			company: '',
			description: '',
			endedAt: new Date(),
			jobTitle: '',
			workLocation: '',
			startedFrom: new Date(),
		},
	});

	const createWorkExperienceFn = useMutation({
		mutationKey: 'addWorkExperienceDetail',
		mutationFn: createUserWorkExperienceFn,
		onSuccess(data) {
			queryClient.refetchQueries(['getUserProfile']);
			toast({
				title: 'Work Experience detail added successfully',
				duration: 3000,
				type: 'success',
			});
			onClose();
			// Once created then reseting state to initial state
			setUserWorkExperience({
				updateInfo: {
					company: '',
					description: '',
					endedAt: new Date(),
					jobTitle: '',
					workLocation: '',
					startedFrom: new Date(),
				},
			});
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});

			onClose();
		},
	});

	const editWorkExperienceFn = useMutation({
		mutationKey: 'editWorkExpeienceDetail',
		mutationFn: editUserWorkExperienceFn,
		onSuccess(data) {
			queryClient.refetchQueries(['getUserProfile']);
			toast({
				title: 'Work Experience detail updated successfully',
				duration: 3000,
				type: 'success',
			});
			onClose();
			setUserWorkExperience({
				updateInfo: {
					company: '',
					description: '',
					endedAt: new Date(),
					jobTitle: '',
					workLocation: '',
					startedFrom: new Date(),
				},
			});
		},
		onError(error: Error) {
			toast({
				title: error.message,
				duration: 3000,
				type: 'error',
			});
			onClose();
		},
	});

	const errorToast = () => {
		toast({
			title: 'Please fill all the required fields',
			type: 'error',
		});
	};

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (userWorkExperience.updateInfo.endedAt && userWorkExperience.updateInfo.startedFrom) {
			if (userWorkExperience.updateInfo.startedFrom > userWorkExperience.updateInfo.endedAt) {
				setDateError('start date must be less than end date');
				return;
			}
		}

		if (userWorkExperience.updateInfo.startedFrom) {
			if (checkIsFutureDate(moment(userWorkExperience.updateInfo.startedFrom))) {
				setDateError('Start date cannot be a future date');
				return;
			}
		}

		if (isEditing) {
			editWorkExperienceFn.mutate({
				id: workExId,
				data: userWorkExperience,
			});
		} else {
			if (
				!userWorkExperience.updateInfo.company ||
				!userWorkExperience.updateInfo.jobTitle ||
				!userWorkExperience.updateInfo.startedFrom
			) {
				errorToast();
				return;
			}
			createWorkExperienceFn.mutate(userWorkExperience);
		}
	};

	useEffect(() => {
		setUserWorkExperience({
			updateInfo: {
				company: workExperience.company || '',
				description: workExperience.description || '',
				endedAt: moment(workExperience.endedAt).toDate(),
				jobTitle: workExperience.jobTitle || '',
				workLocation: workExperience.workLocation || '',
				startedFrom: moment(workExperience.startedFrom).toDate(),
			},
		});
		setWorkExId(workExpId || '');
	}, [workExperience, workExpId]);

	useEffect(() => {
		setDateError('');
	}, [isOpen]);

	return (
		<Modal
			dismissible={true}
			show={isOpen}
			onClose={() => {
				onClose();
			}}
			size="lg"
			className="backdrop-blur-[4px]"
		>
			<Modal.Header className="rounded-t-md border-b-0 dark:bg-[#1F2A37]">
				<div>
					<p className="font-inter text-[20px] font-[700] dark:text-white">Work Experience</p>
				</div>
			</Modal.Header>
			<form className="dark:text-white" onSubmit={handleSubmit}>
				<Modal.Body className="dark:bg-[#1F2A37] !pt-[5px]">
					<div>
						<div className="mb-[8px]">
							<div className="mb-[5px]">
								<Label
									className="font-inter text-[14px] font-[400] dark:text-white"
									htmlFor="jobTitle"
									value="Job Title"
								/>
								<span className="text-red-600">*</span>
							</div>
							<TextInput
								style={{
									fontFamily: "'Inter', sans-serif",
								}}
								type="text"
								id="jobTitle"
								name="jobTitle"
								value={userWorkExperience.updateInfo.jobTitle || ''}
								onChange={e =>
									setUserWorkExperience({
										updateInfo: {
											...userWorkExperience.updateInfo,
											jobTitle: e.target.value,
										},
									})
								}
							/>
						</div>
						<div className="mb-[8px]">
							<div className="mb-[5px]">
								<Label
									className="font-inter text-[14px] font-[400] dark:text-white"
									htmlFor="companyId"
									value="Company"
								/>
								<span className="text-red-600">*</span>
							</div>
							<TextInput
								style={{
									fontFamily: "'Inter', sans-serif",
								}}
								type="text"
								id="company"
								name="Company"
								value={userWorkExperience.updateInfo.company || ''}
								onChange={e =>
									setUserWorkExperience({
										updateInfo: {
											...userWorkExperience.updateInfo,
											company: e.target.value,
										},
									})
								}
							/>
						</div>
						<div className="mb-[8px]">
							<div className="mb-[5px]">
								<Label
									className="font-inter text-[14px] font-[400] dark:text-white"
									htmlFor="location"
									value="Location"
								/>
							</div>
							<TextInput
								style={{
									fontFamily: "'Inter', sans-serif",
								}}
								type="text"
								id="location"
								name="Location"
								value={userWorkExperience.updateInfo.workLocation || ''}
								onChange={e =>
									setUserWorkExperience({
										updateInfo: {
											...userWorkExperience.updateInfo,
											workLocation: e.target.value,
										},
									})
								}
							/>
						</div>
						<div className="mb-[8px]">
							<div className="mb-[5px]">
								<Label
									className="font-inter text-[14px] font-[400] dark:text-white"
									htmlFor="description"
									value="Description"
								/>
							</div>
							<Textarea
								style={{
									fontFamily: "'Inter', sans-serif",
									height: '90px',
								}}
								id="description"
								name="description"
								value={userWorkExperience.updateInfo.description || ''}
								onChange={e =>
									setUserWorkExperience({
										updateInfo: {
											...userWorkExperience.updateInfo,
											description: e.target.value,
										},
									})
								}
							/>
						</div>
						<div className="mb-[5px] flex gap-4">
							<div>
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="startedFrom"
										value="Start date"
									/>
									<span className="text-red-600">*</span>
								</div>
								<div className="flex gap-[16px]">
									<TextInput
										style={{
											fontFamily: "'Inter', sans-serif",
										}}
										type="date"
										id="startedFrom"
										name="startedFrom"
										value={moment(userWorkExperience.updateInfo.startedFrom).format('YYYY-MM-DD') || ''}
										onChange={e =>
											setUserWorkExperience({
												updateInfo: {
													...userWorkExperience.updateInfo,
													startedFrom: moment(e.target.value).toDate(),
												},
											})
										}
									/>
								</div>
							</div>

							<div>
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="endedAt"
										value="End date"
									/>
								</div>
								<div className="flex gap-[16px]">
									<TextInput
										style={{
											fontFamily: "'Inter', sans-serif",
										}}
										type="date"
										id="endedAt"
										name="endedAt"
										value={moment(userWorkExperience.updateInfo.endedAt).format('YYYY-MM-DD') || ''}
										onChange={e =>
											setUserWorkExperience({
												updateInfo: {
													...userWorkExperience.updateInfo,
													endedAt: moment(e.target.value).toDate(),
												},
											})
										}
									/>
								</div>
							</div>
						</div>
					</div>

					{dateError && <div className="text-xs font-bold text-red-500">{dateError}</div>}
				</Modal.Body>
				<Modal.Footer className="rounded-b-md border-t-0 dark:bg-[#18222D]">
					<div className="flex w-full justify-end">
						<div className="">
							<Button
								color="purplePlaced"
								className="bg-[#7777E7]"
								type="submit"
								disabled={
									!userWorkExperience.updateInfo.company ||
									!userWorkExperience.updateInfo.jobTitle ||
									!userWorkExperience.updateInfo.startedFrom ||
									editWorkExperienceFn.isLoading ||
									createWorkExperienceFn.isLoading
								}
							>
								{isEditing ? (
									<>{editWorkExperienceFn.isLoading ? <Spinner size="sm" light={true} /> : 'Save'}</>
								) : (
									<>{createWorkExperienceFn.isLoading ? <Spinner size="sm" light={true} /> : 'Save'}</>
								)}
							</Button>
						</div>
					</div>
				</Modal.Footer>
			</form>
		</Modal>
	);
};

export default EditProfileWorkExperience;
